/**
 * The editor of a rich reading block.
 *
 * A *rich reading block* is a `content` block that knows how it presents itself:
 * a pull quote, a note, a captioned figure, a data table, a formula. They are not
 * separate slide types (see `#/lib/richBlockKinds`), so they share one editor —
 * this one — and one stored shape.
 *
 * These fields are deliberately *not* the generic title + body pair: a quote wants
 * an attribution, a note wants a severity, a figure wants a credit, a table wants
 * cells, and a formula wants the symbol keyboard. Showing a creator the fields
 * their block actually has is the difference between a block that looks right and
 * one they fight.
 *
 * It lives outside either studio because *both* studios offer these blocks. It was
 * written in the FlashBook studio and reached only there; the rows have always been
 * one shared list, and an editor each studio kept its own copy of would be the
 * second thing to drift.
 */
import {useCallback, useRef, useState} from 'react'
import {
  Image,
  ScrollView,
  type StyleProp,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  TouchableOpacity,
  View,
} from 'react-native'
import * as ImagePicker from 'expo-image-picker'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import * as Toast from '#/view/com/util/Toast'
import {FlashBookFormulaField} from '#/components/flashbooks/FlashBookFormulaKeyboard'
import {useFirstFieldFocus} from '#/components/studio/StudioFirstFieldFocus'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {type Slide} from '#/types/capsule'

export function RichBlockEditor({
  slide,
  onChange,
  accent,
}: {
  slide: Slide
  onChange: (data: Partial<Slide>) => void
  accent: string
}) {
  const re = useStyles()
  const c = useStudioChrome()
  const {t: l} = useLingui()
  const kind = slide.contentKind ?? 'text'
  const [picking, setPicking] = useState(false)

  const pickImage = useCallback(async () => {
    setPicking(true)
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        quality: 1,
      })
      if (!result.canceled && result.assets[0]) {
        onChange({imageUrl: result.assets[0].uri})
      }
    } catch {
      Toast.show(l`Could not open that image.`, 'circle-exclamation')
    } finally {
      setPicking(false)
    }
  }, [l, onChange])

  if (kind === 'quote') {
    return (
      <>
        <RichField
          label={l`Quotation`}
          value={slide.contentText ?? ''}
          placeholder={l`The passage you want to set apart…`}
          onChange={contentText => onChange({contentText})}
          accent={accent}
        />
        <RichField
          label={l`Attribution (optional)`}
          value={slide.contentAuthor ?? ''}
          placeholder={l`Who said or wrote it`}
          onChange={contentAuthor => onChange({contentAuthor})}
          accent={accent}
          multiline={false}
        />
      </>
    )
  }

  if (kind === 'callout') {
    const tones: {value: NonNullable<Slide['calloutTone']>; label: string}[] = [
      {value: 'info', label: l`Note`},
      {value: 'success', label: l`Good to know`},
      {value: 'warning', label: l`Careful`},
    ]
    return (
      <>
        <RichField
          label={l`Label (optional)`}
          value={slide.contentTitle ?? ''}
          placeholder={l`Note, Tip, Remember…`}
          onChange={contentTitle => onChange({contentTitle})}
          accent={accent}
          multiline={false}
        />
        <View style={re.richField}>
          <Text style={[re.richLabel, {color: accent}]}>
            {l`Tone`.toUpperCase()}
          </Text>
          <View style={re.chipRow}>
            {tones.map(tone => {
              const active = (slide.calloutTone ?? 'info') === tone.value
              return (
                <TouchableOpacity
                  key={tone.value}
                  accessibilityRole="button"
                  accessibilityLabel={tone.label}
                  accessibilityHint={l`Sets how this note is marked`}
                  onPress={() => onChange({calloutTone: tone.value})}
                  style={[
                    re.chip,
                    active && {backgroundColor: accent, borderColor: accent},
                  ]}>
                  <Text style={[re.chipTxt, active && {color: c.onAccent}]}>
                    {tone.label}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </View>
        </View>
        <RichField
          label={l`Text`}
          value={slide.contentText ?? ''}
          placeholder={l`What the learner should know…`}
          onChange={contentText => onChange({contentText})}
          accent={accent}
        />
      </>
    )
  }

  if (kind === 'caption') {
    return (
      <>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={
            slide.imageUrl ? l`Replace picture` : l`Add picture`
          }
          accessibilityHint={l`Opens your device's photo library`}
          onPress={pickImage}
          style={re.richPick}>
          {slide.imageUrl ? (
            <Image
              source={{uri: slide.imageUrl}}
              style={re.richPickThumb}
              resizeMode="cover"
              accessibilityIgnoresInvertColors
            />
          ) : (
            <View style={re.richPickEmpty}>
              <Feather name="image" size={18} color={c.textMuted} />
            </View>
          )}
          <View style={re.richPickText}>
            <Text style={re.richPickTitle}>
              {slide.imageUrl ? l`Replace picture` : l`Add picture`}
            </Text>
            <Text style={re.richPickHint}>
              {picking ? l`Opening…` : l`JPG or PNG from this device`}
            </Text>
          </View>
          {!!slide.imageUrl && (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Remove picture`}
              accessibilityHint={l`Takes the figure out of this block`}
              onPress={() => onChange({imageUrl: undefined})}
              hitSlop={8}>
              <Feather name="x" size={16} color={c.textMuted} />
            </TouchableOpacity>
          )}
        </TouchableOpacity>
        <RichField
          label={l`Caption`}
          value={slide.contentText ?? ''}
          placeholder={l`What the picture shows…`}
          onChange={contentText => onChange({contentText})}
          accent={accent}
        />
        <RichField
          label={l`Credit (optional)`}
          value={slide.contentAuthor ?? ''}
          placeholder={l`Source or photographer`}
          onChange={contentAuthor => onChange({contentAuthor})}
          accent={accent}
          multiline={false}
        />
      </>
    )
  }

  if (kind === 'table') {
    return <TableEditor slide={slide} onChange={onChange} accent={accent} />
  }

  // formula
  return (
    <>
      {/* A formula is notation: the field carries the symbol keyboard, so the
          creator never leaves the block to write one. */}
      <FlashBookFormulaField
        label={l`Formula`}
        value={slide.latex ?? ''}
        // The example shows the syntax rather than a pretty result: what the
        // creator has to learn here is that a fraction is written, not drawn.
        placeholder="e.g.  x = \frac{-b \pm \sqrt{b^2 - 4ac}}{2a}"
        onChange={latex => onChange({latex})}
        accent={accent}
      />
      <RichField
        label={l`Where it applies (optional)`}
        value={slide.contentText ?? ''}
        placeholder={l`The note the learner reads under it…`}
        onChange={contentText => onChange({contentText})}
        accent={accent}
      />
    </>
  )
}

/** A labelled text field in a rich block's editor. */
function RichField({
  label,
  value,
  placeholder,
  onChange,
  multiline = true,
  accent,
}: {
  label: string
  value: string
  placeholder?: string
  onChange: (v: string) => void
  multiline?: boolean
  accent: string
}) {
  /**
   * A quotation, a note or a caption is the block's *content*, so its field has
   * to show the whole thing — from the moment there is something to show. This
   * is the studio's own text field (see `#/components/studio/StudioTextField`):
   * one line while it is empty, growing line by line with the writing, and
   * scrolling past the cap. It used to open at a flat 84px however little was in
   * it, which reads as a box waiting to be filled rather than somewhere to
   * write, and then grew in 24px steps that had nothing to do with the writing.
   *
   * The three fields that are one line by nature — an attribution, a label, a
   * credit — keep the plain input, where Enter is still the end of the field.
   */
  const re = useStyles()
  const c = useStudioChrome()
  /*
   * A one-line field here can be a block's *first* field — a callout opens on
   * its label — so it takes the caret of a block the creator has just added the
   * same way the multiline field does. The field that is only ever a second or
   * third one asks nothing, because `multiline` is what decides.
   */
  const node = useRef<TextInput>(null)
  useFirstFieldFocus(node, !multiline)

  return (
    <View style={re.richField}>
      <Text style={[re.richLabel, {color: accent}]}>{label.toUpperCase()}</Text>
      {multiline ? (
        <StudioTextField
          accessibilityLabel={label}
          accessibilityHint={placeholder}
          style={re.richInput}
          value={value}
          onChangeText={onChange}
          placeholder={placeholder}
          placeholderTextColor={c.textFaint}
          textAlignVertical="top"
          maxHeight={220}
        />
      ) : (
        <TextInput
          ref={node}
          accessibilityLabel={label}
          accessibilityHint={placeholder}
          style={re.richInput}
          value={value}
          onChangeText={onChange}
          placeholder={placeholder}
          placeholderTextColor={c.textFaint}
        />
      )}
    </View>
  )
}

/**
 * One cell of the table grid.
 *
 * A cell is a field like any other, so the block's *first* one takes the caret
 * of a block the creator has just added (see
 * `#/components/studio/StudioFirstFieldFocus`). That is why it is a component
 * rather than an inline `TextInput`: a hook cannot be called from the middle of
 * a map, and "the first cell" is a thing only the grid itself knows.
 */
function TableCell({
  first,
  accessibilityLabel,
  accessibilityHint,
  style,
  value,
  onChangeText,
  placeholder,
  placeholderTextColor,
}: {
  /** Whether this is the grid's first cell — the block's first field. */
  first: boolean
  accessibilityLabel: string
  accessibilityHint: string
  style: StyleProp<TextStyle>
  value: string
  onChangeText: (v: string) => void
  placeholder: string
  placeholderTextColor: string
}) {
  const node = useRef<TextInput>(null)
  useFirstFieldFocus(node, first)

  return (
    <TextInput
      ref={node}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      style={style}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={placeholderTextColor}
    />
  )
}

/**
 * The table's cells.
 *
 * A table is the one rich block that cannot be typed as prose: a creator has to
 * see the grid they are building. Rows and columns are added and removed in
 * place, and the first row is always the header — which is what every table
 * style in the design layer keys off.
 */
function TableEditor({
  slide,
  onChange,
  accent,
}: {
  slide: Slide
  onChange: (data: Partial<Slide>) => void
  accent: string
}) {
  const re = useStyles()
  // Named `chrome`, not `c`: the cells below are mapped with `(cell, c)`, and a
  // shadowed `c` would silently swap a colour for a column index.
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const rows = slide.tableRows?.length
    ? slide.tableRows
    : [
        ['', ''],
        ['', ''],
      ]
  const cols = Math.max(slide.tableCols ?? rows[0]?.length ?? 2, 1)

  const write = useCallback(
    (next: string[][]) => {
      onChange({tableRows: next, tableCols: next[0]?.length ?? cols})
    },
    [cols, onChange],
  )

  const normalize = (row: string[]) =>
    Array.from({length: cols}, (_, i) => row[i] ?? '')

  const setCell = (r: number, c: number, v: string) =>
    write(
      rows.map((row, ri) =>
        ri === r
          ? normalize(row).map((cell, ci) => (ci === c ? v : cell))
          : normalize(row),
      ),
    )

  const addRow = () =>
    write([...rows.map(normalize), Array.from({length: cols}, () => '')])
  const addCol = () => {
    if (cols >= 6) return
    write(rows.map(row => [...normalize(row), '']))
  }
  const removeRow = (r: number) => {
    if (rows.length <= 1) return
    write(rows.filter((_, i) => i !== r).map(normalize))
  }
  const removeCol = (c: number) => {
    if (cols <= 1) return
    write(rows.map(row => normalize(row).filter((_, i) => i !== c)))
  }

  return (
    <View style={re.richField}>
      <Text style={[re.richLabel, {color: accent}]}>
        {l`Rows and columns`.toUpperCase()}
      </Text>
      {/* A table can be wider than a phone column. Rather than squeezing the
          cells until the headings are unreadable, the grid keeps a minimum cell
          width and scrolls sideways when it has to — the same way the table
          itself will once it is printed on a page. */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={re.gridScroll}>
        <View style={re.grid}>
          {rows.map((row, r) => (
            <View key={r} style={re.gridRow}>
              {/* The delete control stays on the *left* of the row, outside the
                  cells that scroll sideways, so removing a row is always one
                  tap away no matter how wide the table gets. */}
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={l`Remove row ${r + 1}`}
                accessibilityHint={l`Deletes this table row`}
                disabled={rows.length <= 1}
                onPress={() => removeRow(r)}
                hitSlop={6}
                style={[re.gridDel, rows.length <= 1 && re.gridDelOff]}>
                <Feather name="minus-circle" size={14} color={chrome.danger} />
              </TouchableOpacity>
              <Text style={[re.gridRowNo, {color: accent}]}>{r + 1}</Text>
              {normalize(row).map((cell, c) => (
                <TableCell
                  key={c}
                  /*
                   * The grid's first cell is the block's first field, so it is
                   * the one that takes the caret of a table the creator has just
                   * added — they asked for a table, and what a table starts with
                   * is its first heading.
                   */
                  first={r === 0 && c === 0}
                  accessibilityLabel={
                    r === 0
                      ? l`Column ${c + 1} heading`
                      : l`Row ${r + 1}, column ${c + 1}`
                  }
                  accessibilityHint={l`Table cell`}
                  style={[re.gridCell, r === 0 && re.gridCellHead]}
                  value={cell}
                  onChangeText={v => setCell(r, c, v)}
                  placeholder={r === 0 ? l`Heading ${c + 1}` : ''}
                  placeholderTextColor={chrome.textFaint}
                />
              ))}
            </View>
          ))}
        </View>
      </ScrollView>
      <View style={re.gridTools}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Add row`}
          accessibilityHint={l`Adds a row under the last one`}
          onPress={addRow}
          style={re.gridTool}>
          <Feather name="plus" size={13} color={chrome.text} />
          <Text style={re.gridToolTxt}>{l`Row`}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Add column`}
          accessibilityHint={l`Adds a column at the end`}
          disabled={cols >= 6}
          onPress={addCol}
          style={[re.gridTool, cols >= 6 && re.gridDelOff]}>
          <Feather name="plus" size={13} color={chrome.text} />
          <Text style={re.gridToolTxt}>{l`Column`}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Remove column`}
          accessibilityHint={l`Deletes the last column`}
          disabled={cols <= 1}
          onPress={() => removeCol(cols - 1)}
          style={[re.gridTool, cols <= 1 && re.gridDelOff]}>
          <Feather name="minus" size={13} color={chrome.text} />
          <Text style={re.gridToolTxt}>{l`Column`}</Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const makeStyles = (c: StudioChrome) =>
  StyleSheet.create({
    // ── Rich reading blocks ──────────────────────────────────────────────────
    richField: {gap: 6, marginBottom: 12},
    richLabel: {fontSize: 10, fontWeight: '800', letterSpacing: 1.1},
    richInput: {
      color: c.text,
      fontSize: 15,
      lineHeight: 22,
      paddingHorizontal: 11,
      paddingVertical: 9,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
      minHeight: 44,
    },
    chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
    chip: {
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    chipTxt: {color: c.text, fontSize: 12.5, fontWeight: '700'},
    richPick: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 11,
      marginBottom: 12,
      padding: 9,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: c.borderStrong,
      backgroundColor: c.surface,
    },
    richPickThumb: {width: 54, height: 42, borderRadius: 8},
    richPickEmpty: {
      width: 54,
      height: 42,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.fillStrong,
    },
    richPickText: {flex: 1, minWidth: 0},
    richPickTitle: {color: c.text, fontSize: 13.5, fontWeight: '700'},
    richPickHint: {color: c.textMuted, fontSize: 11, marginTop: 2},
    gridScroll: {minWidth: '100%'},
    grid: {gap: 6},
    gridRow: {flexDirection: 'row', alignItems: 'center', gap: 6},
    gridRowNo: {width: 14, fontSize: 11, fontWeight: '800'},
    gridCell: {
      flex: 1,
      // Wide enough for a real heading; the grid scrolls rather than truncating.
      minWidth: 86,
      color: c.text,
      fontSize: 13,
      paddingHorizontal: 8,
      paddingVertical: 7,
      borderRadius: 7,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    gridCellHead: {
      fontWeight: '800',
      borderColor: c.borderStrong,
      backgroundColor: c.fillStrong,
    },
    gridDel: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.dangerTint,
    },
    gridDelOff: {opacity: 0.35},
    gridTools: {flexDirection: 'row', gap: 8, marginTop: 10},
    gridTool: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 11,
      paddingVertical: 7,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: c.borderStrong,
      backgroundColor: c.fill,
    },
    gridToolTxt: {color: c.text, fontSize: 12, fontWeight: '700'},
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
